/**
 * LimitsPanel — honest scope statement for graded bridges in the AI
 * safety stack.
 *
 * Mirrors chapter 10 §10.9 (Honest limits) and chapter 1 §1.5 of the
 * manuscript. Graded bridges close one specific structural class
 * (Tier-2 confused deputies at typed handoff seams). They do not
 * replace prompt-injection defenses, evals, content moderation,
 * sandboxes, or constitutional methods — they are multiplicative with
 * all of them.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";

interface FailureCondition {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly defenseInDepth: string;
}

const FAILURE_CONDITIONS: readonly FailureCondition[] = [
  {
    id: "modify-sdp-module",
    title: "Modify the SDP discharge module in MathLibCore",
    body: "An attacker with write access to the type-defining MathLibCore module can redefine DischargesAdjunctionGrade and admit anything. No type-based defense survives modification of the type itself.",
    defenseInDepth:
      "CI postulate budget · Kernel/Foundation layering policy · signed commits · reproducible builds.",
  },
  {
    id: "inject-grade-out-of-band",
    title: "Inject grade values out-of-band (deployment integration leak)",
    body: "An attacker who can write to the deployment-side data plane that synthesizes Grade values from external evidence can simply forge the grade vector. The type system protects only what crosses the typed seam; values manufactured before the seam are not adjudicated.",
    defenseInDepth:
      "Deployment invariant DI-1 (grade values are produced only by typed constructors) · realization conformance tests · cryptographic binding of grade to body.",
  },
  {
    id: "compromise-in-scope-agent",
    title: "Compromise an in-scope agent (Tier-3 attacker)",
    body: "Graded typing limits the blast radius across agents but does not defend against a fully compromised agent acting within its own legitimate grade. A coding agent that has been backdoored to delete its own files does so at its own legitimate grade — no cross-seam crossing happens.",
    defenseInDepth:
      "Process isolation · OS sandboxing · least-privilege capabilities · per-agent monitoring · zero-trust networking between agents.",
  },
  {
    id: "compromise-retrieval-driver",
    title: "Compromise the retrieval driver wrapping untrusted content",
    body: "If the driver that decorates retrieved content with a trusted [Tr, η] grade is itself compromised, the attacker mints arbitrary lineage grades and the downstream type-checker has no reason to refuse them. The driver is in the trusted base.",
    defenseInDepth:
      "Hardened retrieval boundary · driver attestation · content-source pinning · supply-chain verification.",
  },
  {
    id: "runtime-sdp-registration",
    title: "Register a new SDP at runtime with an arbitrary ICG envelope",
    body: "An attacker who can dynamically install an eighth SDP — or rewrite an existing one's ICG envelope — breaks T3's bound on the composite grade. The seven-letter alphabet is frozen by construction; allowing runtime extension would defeat it.",
    defenseInDepth:
      "Closed-world SDP registry · CI ratchet on SDP count · build-time enumeration check · no runtime reflection on the typing kit.",
  },
];

interface StackLayer {
  readonly index: number;
  readonly title: string;
  readonly addressedBy: string;
  readonly highlight: boolean;
  readonly accent: string;
}

const STACK_LAYERS: readonly StackLayer[] = [
  {
    index: 1,
    title: "User input · prompts · retrieved content",
    addressedBy:
      "Content filters · moderation classifiers · provenance pinning",
    highlight: false,
    accent: "#7a8aa6",
  },
  {
    index: 2,
    title: "Model output · jailbreak detection",
    addressedBy: "Constitutional methods · evals · RLHF · refusal training",
    highlight: false,
    accent: "#7a8aa6",
  },
  {
    index: 3,
    title: "Tool / agent / pipeline handoffs (typed seams)",
    addressedBy: "Graded bridges — this demo",
    highlight: true,
    accent: "#22d3ee",
  },
  {
    index: 4,
    title: "Runtime sandboxing · resource quotas",
    addressedBy:
      "OS · hardware capabilities · seccomp · cgroups · syscall filters",
    highlight: false,
    accent: "#7a8aa6",
  },
  {
    index: 5,
    title: "Supply chain · build pipeline · trusted base",
    addressedBy:
      "Reproducible builds · source review · CI ratchets · signed artifacts",
    highlight: false,
    accent: "#7a8aa6",
  },
];

interface BehindRow {
  readonly title: string;
  readonly body: React.ReactNode;
  readonly accent: string;
}

const BEHIND_ROWS: readonly BehindRow[] = [
  {
    title: "Kernel layering policy",
    accent: "#a78bfa",
    body: (
      <>
        The Kernel layering policy (
        <PanelSurface.InlineCode>
          <a
            href="https://github.com/InvariantHoldings/categorical-safety-kernel/blob/main/docs/LAYERING.md"
            className="text-[#a78bfa] underline decoration-dotted"
            rel="noreferrer"
          >
            docs/LAYERING.md
          </a>
        </PanelSurface.InlineCode>
        ) prevents Foundation and Kernel from importing MathLib escape hatches.
        Without that policy a postulate sneaked into MathLib could weaken the
        typing relations the kernel depends on.
      </>
    ),
  },
  {
    title: "CI postulate budget",
    accent: "#22d3ee",
    body: (
      <>
        The CI postulate budget refuses upgrades that weaken typing relations.
        New <PanelSurface.InlineCode>postulate</PanelSurface.InlineCode> declarations in{" "}
        <PanelSurface.InlineCode>agda/Kernel/</PanelSurface.InlineCode> are statically forbidden; budgets
        in <PanelSurface.InlineCode>agda/Foundation/</PanelSurface.InlineCode> only ratchet downward.
      </>
    ),
  },
  {
    title: "Realization conformance tests",
    accent: "#22c55e",
    body: (
      <>
        Realization conformance tests (Python and TypeScript) verify that the
        typed boundary survives runtime — every constructor the Agda kernel says
        must exist actually exists in the realization, and every refusal the
        kernel demands is observed at the realization seam.
      </>
    ),
  },
];

interface ReadNextEntry {
  readonly heading: string;
  readonly description: string;
  readonly accent: string;
}

const READ_NEXT: readonly ReadNextEntry[] = [
  {
    heading: "00-Threat-Model.md",
    description:
      "Threat model — the full Tier-0..Tier-3 attacker taxonomy and what graded bridges close versus leave to other layers.",
    accent: "#ef4444",
  },
  {
    heading: "Chapter 10 §10.9 — Honest limits",
    description:
      "The formal statement of what graded bridges do not defend against, with proof obligations and citations.",
    accent: "#a78bfa",
  },
  {
    heading: "Chapter 9 §9.9 — Adversarial deployment hardening",
    description:
      "Operational checklist for deploying graded bridges alongside sandboxes, monitoring, supply-chain hardening, and evals.",
    accent: "#22c55e",
  },
];

export function LimitsPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Limits & Scope"
      subtitle="HONEST POSITION IN THE AI SAFETY STACK · WHAT THIS DISCIPLINE DOES AND DOES NOT CLOSE"
      accentColor="#7a8aa6"
    >
      <PanelSurface.Card glowColor="#7a8aa644">
        <PanelSurface.SectionHeading>
          What this discipline closes — and what it does not
        </PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[14px] leading-[1.65]">
          Graded bridges close <strong>one specific structural class</strong>:
          Tier-2 confused-deputy attacks at typed handoff seams. They make a
          forbidden phantom unconstructable at the type level. They do not
          replace prompt-injection defenses, model evaluations, content
          moderation, runtime sandboxes, or constitutional methods. They sit{" "}
          <em>alongside</em> all of those. The right mental model is{" "}
          <strong>multiplication</strong>: each layer of the defense stack
          removes some fraction of the residual attack surface, and the combined
          system is safe only when every layer functions.
        </p>
        <div className="mt-3.5 flex flex-wrap gap-2">
          {PanelSurface.badge("closes · Tier-2 confused deputies at typed seams", "#22c55e")}
          {PanelSurface.badge("does not close · prompt injection at the model", "#fbbf24")}
          {PanelSurface.badge("does not close · in-scope agent compromise", "#fbbf24")}
          {PanelSurface.badge(
            "does not close · supply-chain / trusted-base compromise",
            "#fbbf24",
          )}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Fails if attacker can…</PanelSurface.SectionHeading>
        <p className="mt-2 mb-3.5 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
          Five concrete failure conditions. Each one names a defense-in-depth
          control that addresses it. None of these are graded-bridge
          responsibilities; all of them must be present for the deployment to be
          safe.
        </p>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-2.5">
          {FAILURE_CONDITIONS.map((condition, index) => (
            <div
              key={condition.id}
              className="grid grid-cols-[42px_minmax(0,1fr)] gap-3.5 rounded-xl border border-[var(--tcc-border)] bg-[linear-gradient(135deg,rgba(239,68,68,0.08),rgba(5,8,16,0.55))] px-4 py-3.5"
              style={{
                borderLeft: "3px solid #ef4444",
              }}
            >
              <div
                aria-hidden
                className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-[#ef444466] bg-[rgba(239,68,68,0.14)] font-mono text-[16px] font-extrabold text-[#ef4444]"
              >
                {index + 1}
              </div>
              <div>
                <div className="text-[14px] font-bold leading-[1.35] text-[var(--tcc-bright)]">
                  {condition.title}
                </div>
                <div className="mt-1.5 text-[13px] leading-[1.6] text-[var(--tcc-text)]">
                  {condition.body}
                </div>
                <div className="mt-2.5 border-t border-dashed border-[var(--tcc-border)] pt-2 text-[12.5px] leading-[1.5] text-[var(--tcc-muted)]">
                  <span className="mr-1.5 font-mono font-bold tracking-[0.05em] text-[#22c55e]">
                    Defense in depth:
                  </span>
                  {condition.defenseInDepth}
                </div>
              </div>
            </div>
          ))}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Stack position</PanelSurface.SectionHeading>
        <p className="mt-2 mb-4 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
          Graded bridges sit at the typed-handoff layer. The layers above and
          below are not in scope — they are addressed by complementary
          disciplines.
        </p>
        <div
          role="list"
          aria-label="AI safety stack layers"
          className="flex flex-col gap-2"
        >
          {STACK_LAYERS.map((layer) => (
            <div
              key={layer.index}
              role="listitem"
              className="grid grid-cols-[56px_minmax(0,1fr)] items-center gap-3.5 rounded-xl border border-solid"
              style={{
                padding: layer.highlight ? "16px 18px" : "12px 16px",
                borderColor: layer.highlight ? `${layer.accent}88` : panelTokens.border,
                background: layer.highlight
                  ? `linear-gradient(135deg, ${layer.accent}24, rgba(5,8,16,0.55))`
                  : "rgba(5,8,16,0.4)",
                boxShadow: layer.highlight
                  ? `0 0 24px ${layer.accent}55, inset 0 0 0 1px ${layer.accent}55`
                  : "none",
                transition: "all 140ms ease",
              }}
            >
              <div
                aria-hidden
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-solid font-mono text-[17px] font-extrabold"
                style={{
                  borderColor: `${layer.accent}88`,
                  background: layer.highlight
                    ? `${layer.accent}33`
                    : "rgba(5,8,16,0.6)",
                  color: layer.accent,
                  textShadow: layer.highlight
                    ? `0 0 12px ${layer.accent}99`
                    : "none",
                }}
              >
                L{layer.index}
              </div>
              <div>
                <div
                  className="leading-[1.35]"
                  style={{
                    fontSize: layer.highlight ? 15 : 14,
                    fontWeight: layer.highlight ? 800 : 700,
                    color: layer.highlight ? panelTokens.bright : panelTokens.text,
                  }}
                >
                  {layer.title}
                </div>
                <div
                  className="mt-[3px] font-mono text-[12.5px] leading-[1.5] tracking-[0.02em]"
                  style={{
                    color: layer.highlight ? layer.accent : panelTokens.muted,
                  }}
                >
                  → {layer.addressedBy}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3.5 text-[12.5px] italic leading-[1.55] text-[var(--tcc-muted)]">
          The highlighted middle layer is the only one this demo is responsible
          for. The four flanking layers must each be addressed by their own
          discipline; absent any of them, the deployment is not safe even with
          perfect graded typing at L3.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>What sits behind this discipline</PanelSurface.SectionHeading>
        <p className="mt-2 mb-3.5 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
          Three repository-level disciplines guard the trusted base that graded
          bridges depend on.
        </p>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-2.5">
          {BEHIND_ROWS.map((row) => (
            <div
              key={row.title}
              className="rounded-xl border border-solid px-3.5 py-3"
              style={{
                borderColor: `${row.accent}55`,
                background: `linear-gradient(135deg, ${row.accent}10, rgba(5,8,16,0.55))`,
              }}
            >
              <div
                className="mb-1 font-mono text-[11px] font-bold uppercase tracking-[0.08em]"
                style={{ color: row.accent }}
              >
                {row.title}
              </div>
              <div className="text-[13px] leading-[1.6] text-[var(--tcc-text)]">
                {row.body}
              </div>
            </div>
          ))}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Where to read next</PanelSurface.SectionHeading>
        <div className="mt-3 grid grid-cols-[minmax(0,1fr)] gap-2.5">
          {READ_NEXT.map((entry) => (
            <div
              key={entry.heading}
              className="grid grid-cols-[8px_minmax(0,1fr)] items-stretch gap-3.5 rounded-xl border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.45)] px-3.5 py-3"
            >
              <div
                aria-hidden
                className="rounded"
                style={{
                  background: `linear-gradient(180deg, ${entry.accent}, transparent)`,
                }}
              />
              <div>
                <div className="font-mono text-[13.5px] font-bold tracking-[0.02em] text-[var(--tcc-bright)]">
                  {entry.heading}
                </div>
                <div className="mt-1 text-[13px] leading-[1.55] text-[var(--tcc-text)]">
                  {entry.description}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3.5 text-[12.5px] italic leading-[1.55] text-[var(--tcc-muted)]">
          The honest position is that no single discipline closes the AI safety
          problem. Graded bridges close one well-defined class completely, at
          the type level, by construction — and that contribution is
          multiplicative with every other layer of the stack.
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
